import { useSnackbar } from "notistack";
import {
  ApplyExceptionForScopesInput,
  ExceptionMutationAction,
  ExceptionMutationInput,
  ListExceptionGroupsDocument,
  useCreateExceptionForScopesMutation,
  useCreateExceptionMutation,
  useDeleteExceptionsMutation,
} from "~/operations";
import { InternalRefetchQueryDescriptor } from "@apollo/client/core/types";
import { ExceptionFormInput } from "~/components/exceptions/SetExceptionDialog";
import { add } from "~/lib/date";
import { getGraphQLErrors } from "../ui-library";
import { ExceptionFormValidUntil } from "./hooks/useSetExceptionDialog";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { useSearchParams } from "react-router-dom";

type UseExceptionsParams = {
  onSetException?: () => void;
  scopeMrns: string[];
  advisoryMrns?: Array<string>;
  cveMrns?: Array<string>;
  controlMrns?: Array<string>;
  queryMrns?: Array<string>;
  fexMrns?: Array<string>;
  refetchQueries: InternalRefetchQueryDescriptor[];
  multipleScopes?: boolean;
  applyToCves?: boolean;
};

export const validUntilDateString = (
  value: ExceptionFormValidUntil,
  customDays?: number,
): string | undefined => {
  const now = new Date();
  switch (value) {
    case ExceptionFormValidUntil.Custom:
      return add(now, { days: customDays }).toISOString();
    case ExceptionFormValidUntil.Week1:
      return add(now, { weeks: 1 }).toISOString();
    case ExceptionFormValidUntil.Month1:
      return add(now, { months: 1 }).toISOString();
    case ExceptionFormValidUntil.Month3:
      return add(now, { months: 3 }).toISOString();
    case ExceptionFormValidUntil.Month6:
      return add(now, { months: 6 }).toISOString();
    case ExceptionFormValidUntil.Indefinitely:
    default:
      return undefined;
  }
};

export function useExceptions({
  onSetException,
  scopeMrns,
  advisoryMrns,
  cveMrns,
  queryMrns,
  fexMrns,
  controlMrns,
  refetchQueries,
  multipleScopes = false,
  applyToCves = false,
}: UseExceptionsParams) {
  const [searchParams] = useSearchParams();
  const [createException, createExceptionResult] = useCreateExceptionMutation();
  const [createExceptionForScope, createExceptionForScopeResult] =
    useCreateExceptionForScopesMutation();
  const [deleteExceptions] = useDeleteExceptionsMutation();
  const { enqueueSnackbar } = useSnackbar();

  const baseSetException = async ({
    input,
  }: {
    input: ExceptionMutationInput | ApplyExceptionForScopesInput;
  }) => {
    try {
      if (multipleScopes) {
        // check if scope is already in the input
        const scopeToPass =
          "scopeMrns" in input && input.scopeMrns.length > 0
            ? input.scopeMrns
            : scopeMrns;
        await createExceptionForScope({
          variables: { input: { ...input, scopeMrns: scopeToPass } },
          refetchQueries: [...refetchQueries],
          update: (cache) => {
            cache.evict({ fieldName: "listExceptionGroups" });
          },
          awaitRefetchQueries: true,
          notifyOnNetworkStatusChange: true,
        });
      } else {
        const scopeToPass =
          "scopeMrn" in input && input.scopeMrn.length > 0
            ? input.scopeMrn
            : scopeMrns[0];

        const { data: exceptionData } = await createException({
          variables: {
            input: { ...input, scopeMrn: scopeToPass, applyToCves },
          },
          refetchQueries: [...refetchQueries],
          update: (cache) => {
            cache.evict({ fieldName: "listExceptionGroups" });
          },
          awaitRefetchQueries: true,
          notifyOnNetworkStatusChange: true,
        });

        if (exceptionData?.createException.exceptionGroup.exceptionId) {
          enqueueSnackbar(`Exception set successfully`, {
            variant: "success",
            persist: true,
            button: {
              label: "OPEN EXCEPTION",
              href: getExceptionLink({
                id: exceptionData?.createException.exceptionGroup.exceptionId,
                queryParams: searchParams,
              }),
            },
          });
        }
      }
      onSetException?.();
    } catch (error) {
      enqueueSnackbar(
        getGraphQLErrors({
          error,
          errorMessage: "Failed to set exception",
        }),
        { variant: "error" },
      );
    }
  };

  const handleSetException = async (formData: ExceptionFormInput) => {
    const input: ExceptionMutationInput = {
      scopeMrn: formData.scope ? formData.scope : scopeMrns[0],
      controlMrns,
      queryMrns,
      fexMrns,
      advisoryMrns,
      cveMrns,
      action: formData.action,
      justification: formData.justification,
      validUntil: validUntilDateString(
        formData.validUntil,
        formData.customValidUntil,
      ),
    };

    return baseSetException({ input });
  };

  const handleSetExceptionForScopes = async (formData: ExceptionFormInput) => {
    const input: ApplyExceptionForScopesInput = {
      scopeMrns,
      controlMrns,
      queryMrns,
      fexMrns,
      advisoryMrns,
      cveMrns,
      applyToCves,
      action: formData.action,
      justification: formData.justification,
      validUntil: validUntilDateString(
        formData.validUntil,
        formData.customValidUntil,
      ),
    };

    return baseSetException({ input });
  };

  const baseSetScope = async ({
    input,
  }: {
    input: ExceptionMutationInput | ApplyExceptionForScopesInput;
  }) => {
    try {
      if (multipleScopes) {
        await createExceptionForScope({
          variables: {
            input: { ...input, scopeMrns },
          },
          refetchQueries: [ListExceptionGroupsDocument, ...refetchQueries],
          awaitRefetchQueries: true,
          notifyOnNetworkStatusChange: true,
        });
      } else {
        await createException({
          variables: { input: { ...input, scopeMrn: scopeMrns[0] } },
          refetchQueries: [ListExceptionGroupsDocument, ...refetchQueries],
          awaitRefetchQueries: true,
          notifyOnNetworkStatusChange: true,
        });
      }
      onSetException?.();
    } catch (error) {
      enqueueSnackbar(`Failed to set exception`, {
        variant: "error",
      });
    }
  };

  const handleSetScope = async (isInScope: boolean) => {
    const input: ExceptionMutationInput = {
      scopeMrn: scopeMrns[0],
      controlMrns: controlMrns,
      action: isInScope
        ? ExceptionMutationAction.Enable
        : ExceptionMutationAction.OutOfScope,
    };

    await baseSetScope({ input });
  };

  const handleSetScopeForMultipleScopes = async (isInScope: boolean) => {
    const input: ApplyExceptionForScopesInput = {
      scopeMrns: scopeMrns,
      controlMrns: controlMrns,
      action: isInScope
        ? ExceptionMutationAction.Enable
        : ExceptionMutationAction.OutOfScope,
    };

    await baseSetScope({ input });
  };

  const handleRemoveExceptionGroups = async ({
    exceptionMrns,
    successMessage = "Exception removed successfully",
    errorMessage = "Failed to remove exception",
  }: {
    exceptionMrns: string[];
    successMessage?: string;
    errorMessage?: string;
  }) => {
    try {
      await deleteExceptions({
        variables: {
          input: {
            exceptionMrns,
          },
        },
        // TODO: because we're always deleting exception groups, i'm not sure if there are
        // other queries that still need to be refetched, but i'm leaving the spread for now
        refetchQueries: [...refetchQueries],
        update: (cache) => {
          cache.evict({ fieldName: "listExceptionGroups" });
          cache.evict({ fieldName: "getAggregateScores" });
        },
        awaitRefetchQueries: true,
        notifyOnNetworkStatusChange: true,
      });

      enqueueSnackbar(successMessage, {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  return {
    handleRemoveExceptionGroups,
    handleSetException,
    handleSetExceptionForScopes,
    handleSetScope,
    handleSetScopeForMultipleScopes,
    loading:
      createExceptionResult.loading || createExceptionForScopeResult.loading,
  };
}
